/**
 * WeaveIn.ai Platform - Workforce Case Detail Component
 * Comprehensive case management view with 8 tabs:
 * Overview, Activities, Documents, Notes, Goals, Tracking, Communications, Assessments & Forms
 *
 * Follows the EnrollmentDetailNew.tsx pattern with Tailwind CSS and Radix UI primitives.
 */

import React, { useState, useEffect } from 'react';
import {
  ArrowLeft as BackIcon,
  Plus as AddIcon,
  FileText as DocumentIcon,
  StickyNote as NoteIcon,
  Flag as GoalIcon,
  Phone as PhoneIcon,
  Mail as EmailIcon,
  MessageSquare as MessageIcon,
  CheckCircle as CheckCircleIcon,
  Clock as ClockIcon,
  Loader2,
  Briefcase,
  User,
  Calendar,
  AlertCircle,
  Activity,
  ClipboardList,
  Target,
  ListTodo,
  Send,
  ArrowUpCircle,
  ArrowDownCircle,
  BarChart3,
  FileCheck,
  PlayCircle,
  CircleDot,
  Info,
  RefreshCw,
} from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { Textarea } from '@/components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

import { getWeaveinApiUrl } from '@/src/config';
import { authFetch } from '@/lib/authFetch';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface CaseDetailProps {
  caseId: number;
  onBack?: () => void;
}

interface CaseData {
  case_id: number;
  case_number: string;
  case_type: string;
  case_status: string;
  title: string;
  description: string | null;
  subject_name: string | null;
  subject_user_id: number | null;
  assigned_to_name: string | null;
  assigned_to_user_id: number | null;
  priority: string;
  opened_date: string | null;
  target_resolution_date: string | null;
  closed_date: string | null;
  pending_tasks: number;
  notes_count: number;
  documents_count: number;
  days_open: number;
  program_id: number | null;
  program_name: string | null;
  program_type: string | null;
  workflow_template_name: string | null;
  workflow_progress: number | null;
}

interface TimelineEvent {
  event_id: number;
  event_type: string;
  description: string;
  actor_name: string | null;
  event_date: string;
  metadata?: Record<string, unknown>;
}

interface CaseDocument {
  document_id: number;
  document_type: string;
  file_name: string;
  status: string;
  expiration_date: string | null;
  notes: string | null;
  created_at: string | null;
  created_by_name: string | null;
}

interface CaseNote {
  note_id: number;
  note_text: string;
  note_type: string;
  created_at: string | null;
  created_by_name: string | null;
}

interface CaseGoal {
  goal_id: number;
  goal_text: string;
  goal_category: string;
  goal_type: string | null;
  status: string;
  progress: number;
  target_date: string | null;
  completed_date: string | null;
  created_at: string | null;
}

interface WorkflowInstance {
  instance_id: number;
  template_name: string;
  status: string;
  progress: number;
  total_steps: number;
  completed_steps: number;
  started_at: string | null;
  completed_at: string | null;
}

interface CaseTask {
  task_id: number;
  task_title: string;
  task_type: string;
  due_date: string | null;
  priority: string;
  status: string;
  assigned_to_name: string | null;
  is_overdue: boolean;
}

interface Communication {
  communication_id: number;
  communication_type: string;
  direction: string;
  subject: string | null;
  content: string | null;
  contact_name: string | null;
  outcome: string | null;
  created_at: string | null;
  created_by_name: string | null;
}

interface Assessment {
  assessment_id: number;
  assessment_type: string;
  assessment_name: string;
  score: number | null;
  max_score: number | null;
  pass_fail: string | null;
  status: string;
  completed_date: string | null;
  assessor_name: string | null;
}

interface CaseForm {
  form_id: number;
  form_name: string;
  form_type: string;
  status: string;
  submitted_date: string | null;
  submitted_by_name: string | null;
}

// ---------------------------------------------------------------------------
// API Helper
// ---------------------------------------------------------------------------

const fetchAPI = async (url: string, options?: RequestInit) => {
  const res = await authFetch(`${getWeaveinApiUrl()}${url}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options?.headers,
    },
  });
  if (!res.ok) throw new Error(`API error: ${res.status}`);
  return res.json();
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const formatDate = (dateStr: string | null | undefined): string => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString();
};

const formatDateTime = (dateStr: string | null | undefined): string => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleString();
};

const getStatusBadgeVariant = (
  status: string
): 'default' | 'primary' | 'success' | 'warning' | 'danger' | 'secondary' | 'info' | 'outline' => {
  switch (status?.toLowerCase()) {
    case 'open':
      return 'primary';
    case 'in_progress':
    case 'in progress':
      return 'warning';
    case 'pending_review':
    case 'pending review':
      return 'secondary';
    case 'resolved':
    case 'completed':
    case 'passed':
      return 'success';
    case 'closed':
      return 'default';
    case 'cancelled':
    case 'failed':
    case 'overdue':
      return 'danger';
    case 'active':
      return 'info';
    default:
      return 'outline';
  }
};

const getPriorityBadgeVariant = (
  priority: string
): 'default' | 'primary' | 'success' | 'warning' | 'danger' | 'secondary' | 'info' | 'outline' => {
  switch (priority?.toLowerCase()) {
    case 'urgent':
      return 'danger';
    case 'high':
      return 'warning';
    case 'normal':
    case 'medium':
      return 'primary';
    case 'low':
      return 'default';
    default:
      return 'outline';
  }
};

const getCaseTypeVariant = (
  caseType: string
): 'default' | 'primary' | 'success' | 'warning' | 'danger' | 'secondary' | 'info' | 'outline' => {
  switch (caseType?.toLowerCase()) {
    case 'recruitment':
      return 'primary';
    case 'training':
      return 'secondary';
    case 'certification':
      return 'success';
    case 'supervision':
      return 'warning';
    case 'onboarding':
      return 'info';
    default:
      return 'outline';
  }
};

const getTimelineEventIcon = (eventType: string) => {
  switch (eventType) {
    case 'created':
      return <PlayCircle className="h-4 w-4 text-blue-500" />;
    case 'status_changed':
      return <RefreshCw className="h-4 w-4 text-yellow-500" />;
    case 'note_added':
      return <NoteIcon className="h-4 w-4 text-purple-500" />;
    case 'communication':
      return <MessageIcon className="h-4 w-4 text-green-500" />;
    case 'assessment':
      return <ClipboardList className="h-4 w-4 text-orange-500" />;
    case 'task_completed':
      return <CheckCircleIcon className="h-4 w-4 text-emerald-500" />;
    case 'workflow_progress':
      return <BarChart3 className="h-4 w-4 text-cyan-500" />;
    default:
      return <CircleDot className="h-4 w-4 text-gray-400" />;
  }
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const CaseDetail: React.FC<CaseDetailProps> = ({ caseId, onBack }) => {
  // Tab state
  const [activeTab, setActiveTab] = useState('overview');

  // Core data
  const [caseData, setCaseData] = useState<CaseData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Tab-specific data
  const [timeline, setTimeline] = useState<TimelineEvent[]>([]);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [timelineError, setTimelineError] = useState<string | null>(null);

  const [documents, setDocuments] = useState<CaseDocument[]>([]);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [documentsError, setDocumentsError] = useState<string | null>(null);

  const [notes, setNotes] = useState<CaseNote[]>([]);
  const [notesLoading, setNotesLoading] = useState(false);
  const [notesError, setNotesError] = useState<string | null>(null);

  const [goals, setGoals] = useState<CaseGoal[]>([]);
  const [goalsLoading, setGoalsLoading] = useState(false);
  const [goalsError, setGoalsError] = useState<string | null>(null);

  const [workflows, setWorkflows] = useState<WorkflowInstance[]>([]);
  const [tasks, setTasks] = useState<CaseTask[]>([]);
  const [trackingLoading, setTrackingLoading] = useState(false);
  const [trackingError, setTrackingError] = useState<string | null>(null);

  const [communications, setCommunications] = useState<Communication[]>([]);
  const [communicationsLoading, setCommunicationsLoading] = useState(false);
  const [communicationsError, setCommunicationsError] = useState<string | null>(null);

  const [assessments, setAssessments] = useState<Assessment[]>([]);
  const [forms, setForms] = useState<CaseForm[]>([]);
  const [assessmentsLoading, setAssessmentsLoading] = useState(false);
  const [assessmentsError, setAssessmentsError] = useState<string | null>(null);

  // Dialog states
  const [documentDialogOpen, setDocumentDialogOpen] = useState(false);
  const [noteDialogOpen, setNoteDialogOpen] = useState(false);
  const [goalDialogOpen, setGoalDialogOpen] = useState(false);
  const [communicationDialogOpen, setCommunicationDialogOpen] = useState(false);
  const [assessmentDialogOpen, setAssessmentDialogOpen] = useState(false);

  // Dialog form data
  const [documentForm, setDocumentForm] = useState({
    document_type: 'general',
    file_name: '',
    notes: '',
  });

  const [noteForm, setNoteForm] = useState({
    note_text: '',
    note_type: 'general',
  });

  const [goalForm, setGoalForm] = useState({
    goal_text: '',
    goal_category: 'professional',
    target_date: '',
    goal_type: 'short_term',
  });

  const [communicationForm, setCommunicationForm] = useState({
    communication_type: 'email',
    direction: 'outbound',
    subject: '',
    content: '',
    contact_name: '',
    outcome: '',
  });

  const [assessmentForm, setAssessmentForm] = useState({
    assessment_type: 'skills',
    assessment_name: '',
    score: '',
    max_score: '100',
    pass_fail: '',
  });

  // Submission loading states
  const [submitting, setSubmitting] = useState(false);

  // Track which tabs have been loaded
  const [loadedTabs, setLoadedTabs] = useState<Set<string>>(new Set());

  // ---------------------------------------------------------------------------
  // Data fetching
  // ---------------------------------------------------------------------------

  // Fetch core case data on mount
  useEffect(() => {
    const fetchCaseData = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await fetchAPI(`/api/workforce/cases/${caseId}`);
        setCaseData(data);
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : 'Failed to load case details';
        setError(message);
      } finally {
        setLoading(false);
      }
    };

    fetchCaseData();
  }, [caseId]);

  // Lazy load tab data when tab changes
  useEffect(() => {
    if (loadedTabs.has(activeTab)) return;

    const markLoaded = () => {
      setLoadedTabs((prev) => new Set(prev).add(activeTab));
    };

    switch (activeTab) {
      case 'activities':
        fetchTimeline().then(markLoaded);
        break;
      case 'documents':
        fetchDocuments().then(markLoaded);
        break;
      case 'notes':
        fetchNotes().then(markLoaded);
        break;
      case 'goals':
        fetchGoals().then(markLoaded);
        break;
      case 'tracking':
        fetchTracking().then(markLoaded);
        break;
      case 'communications':
        fetchCommunications().then(markLoaded);
        break;
      case 'assessments':
        fetchAssessmentsAndForms().then(markLoaded);
        break;
      default:
        break;
    }
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchTimeline = async () => {
    setTimelineLoading(true);
    setTimelineError(null);
    try {
      const data = await fetchAPI(`/api/workforce/cases/${caseId}/timeline`);
      setTimeline(Array.isArray(data) ? data : data.items || []);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load timeline';
      setTimelineError(message);
    } finally {
      setTimelineLoading(false);
    }
  };

  const fetchDocuments = async () => {
    setDocumentsLoading(true);
    setDocumentsError(null);
    try {
      const data = await fetchAPI(`/api/workforce/cases/${caseId}/documents`);
      setDocuments(Array.isArray(data) ? data : data.items || []);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load documents';
      setDocumentsError(message);
    } finally {
      setDocumentsLoading(false);
    }
  };

  const fetchNotes = async () => {
    setNotesLoading(true);
    setNotesError(null);
    try {
      const data = await fetchAPI(`/api/workforce/cases/${caseId}/notes`);
      setNotes(Array.isArray(data) ? data : data.items || []);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load notes';
      setNotesError(message);
    } finally {
      setNotesLoading(false);
    }
  };

  const fetchGoals = async () => {
    setGoalsLoading(true);
    setGoalsError(null);
    try {
      const data = await fetchAPI(`/api/workforce/cases/${caseId}/goals`);
      setGoals(Array.isArray(data) ? data : data.items || []);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load goals';
      setGoalsError(message);
    } finally {
      setGoalsLoading(false);
    }
  };

  const fetchTracking = async () => {
    setTrackingLoading(true);
    setTrackingError(null);
    try {
      const [workflowData, taskData] = await Promise.all([
        fetchAPI(`/api/workforce/cases/${caseId}/workflows`).catch(() => []),
        fetchAPI(`/api/workforce/cases/${caseId}/tasks`).catch(() => []),
      ]);
      setWorkflows(Array.isArray(workflowData) ? workflowData : workflowData.items || []);
      setTasks(Array.isArray(taskData) ? taskData : taskData.items || []);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load tracking data';
      setTrackingError(message);
    } finally {
      setTrackingLoading(false);
    }
  };

  const fetchCommunications = async () => {
    setCommunicationsLoading(true);
    setCommunicationsError(null);
    try {
      const data = await fetchAPI(`/api/workforce/cases/${caseId}/communications`);
      setCommunications(Array.isArray(data) ? data : data.items || []);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load communications';
      setCommunicationsError(message);
    } finally {
      setCommunicationsLoading(false);
    }
  };

  const fetchAssessmentsAndForms = async () => {
    setAssessmentsLoading(true);
    setAssessmentsError(null);
    try {
      const [assessmentData, formData] = await Promise.all([
        fetchAPI(`/api/workforce/cases/${caseId}/assessments`).catch(() => []),
        fetchAPI(`/api/workforce/cases/${caseId}/forms`).catch(() => []),
      ]);
      setAssessments(Array.isArray(assessmentData) ? assessmentData : assessmentData.items || []);
      setForms(Array.isArray(formData) ? formData : formData.items || []);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load assessments';
      setAssessmentsError(message);
    } finally {
      setAssessmentsLoading(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Form submission handlers
  // ---------------------------------------------------------------------------

  const handleAddDocument = async () => {
    if (!documentForm.file_name.trim()) return;
    setSubmitting(true);
    try {
      await fetchAPI(`/api/workforce/cases/${caseId}/documents`, {
        method: 'POST',
        body: JSON.stringify(documentForm),
      });
      setDocumentDialogOpen(false);
      setDocumentForm({ document_type: 'general', file_name: '', notes: '' });
      setLoadedTabs((prev) => {
        const next = new Set(prev);
        next.delete('documents');
        return next;
      });
      fetchDocuments();
    } catch (err) {
      console.error('Error adding document:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddNote = async () => {
    if (!noteForm.note_text.trim()) return;
    setSubmitting(true);
    try {
      await fetchAPI(`/api/workforce/cases/${caseId}/notes`, {
        method: 'POST',
        body: JSON.stringify(noteForm),
      });
      setNoteDialogOpen(false);
      setNoteForm({ note_text: '', note_type: 'general' });
      setLoadedTabs((prev) => {
        const next = new Set(prev);
        next.delete('notes');
        return next;
      });
      fetchNotes();
    } catch (err) {
      console.error('Error adding note:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddGoal = async () => {
    if (!goalForm.goal_text.trim()) return;
    setSubmitting(true);
    try {
      await fetchAPI(`/api/workforce/cases/${caseId}/goals`, {
        method: 'POST',
        body: JSON.stringify({
          ...goalForm,
          target_date: goalForm.target_date || null,
        }),
      });
      setGoalDialogOpen(false);
      setGoalForm({ goal_text: '', goal_category: 'professional', target_date: '', goal_type: 'short_term' });
      setLoadedTabs((prev) => {
        const next = new Set(prev);
        next.delete('goals');
        return next;
      });
      fetchGoals();
    } catch (err) {
      console.error('Error adding goal:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleLogCommunication = async () => {
    if (!communicationForm.content?.trim()) return;
    setSubmitting(true);
    try {
      await fetchAPI(`/api/workforce/cases/${caseId}/communications`, {
        method: 'POST',
        body: JSON.stringify(communicationForm),
      });
      setCommunicationDialogOpen(false);
      setCommunicationForm({
        communication_type: 'email',
        direction: 'outbound',
        subject: '',
        content: '',
        contact_name: '',
        outcome: '',
      });
      setLoadedTabs((prev) => {
        const next = new Set(prev);
        next.delete('communications');
        return next;
      });
      fetchCommunications();
    } catch (err) {
      console.error('Error logging communication:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddAssessment = async () => {
    if (!assessmentForm.assessment_name.trim()) return;
    setSubmitting(true);
    try {
      await fetchAPI(`/api/workforce/cases/${caseId}/assessments`, {
        method: 'POST',
        body: JSON.stringify({
          ...assessmentForm,
          score: assessmentForm.score ? parseFloat(assessmentForm.score) : null,
          max_score: assessmentForm.max_score ? parseFloat(assessmentForm.max_score) : null,
          pass_fail: assessmentForm.pass_fail || null,
        }),
      });
      setAssessmentDialogOpen(false);
      setAssessmentForm({
        assessment_type: 'skills',
        assessment_name: '',
        score: '',
        max_score: '100',
        pass_fail: '',
      });
      setLoadedTabs((prev) => {
        const next = new Set(prev);
        next.delete('assessments');
        return next;
      });
      fetchAssessmentsAndForms();
    } catch (err) {
      console.error('Error adding assessment:', err);
    } finally {
      setSubmitting(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Loading / Error / Not Found states
  // ---------------------------------------------------------------------------

  if (loading) {
    return (
      <div className="p-8">
        <div className="flex items-center justify-center space-x-2">
          <Loader2 className="h-6 w-6 animate-spin text-emerald-600" />
          <p className="text-sm text-muted-foreground">Loading case details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8 text-center">
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>
            <p className="font-semibold mb-1">Error loading case</p>
            <p className="text-sm">{error}</p>
          </AlertDescription>
        </Alert>
        {onBack && (
          <Button onClick={onBack} variant="outline">
            <BackIcon className="h-4 w-4 mr-2" />
            Go Back
          </Button>
        )}
      </div>
    );
  }

  if (!caseData) {
    return (
      <div className="p-8 text-center">
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>
            <p className="font-semibold mb-1">Case not found</p>
            <p className="text-sm">The requested case could not be found.</p>
          </AlertDescription>
        </Alert>
        {onBack && (
          <Button onClick={onBack} variant="outline">
            <BackIcon className="h-4 w-4 mr-2" />
            Go Back
          </Button>
        )}
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Tab content renderers
  // ---------------------------------------------------------------------------

  const renderTabLoading = () => (
    <div className="flex items-center justify-center py-12">
      <Loader2 className="h-6 w-6 animate-spin text-emerald-600" />
      <span className="ml-2 text-sm text-muted-foreground">Loading...</span>
    </div>
  );

  const renderTabError = (message: string, retryFn?: () => void) => (
    <div className="py-8 text-center">
      <AlertCircle className="h-8 w-8 text-red-400 mx-auto mb-2" />
      <p className="text-sm text-muted-foreground mb-3">{message}</p>
      {retryFn && (
        <Button variant="outline" size="sm" onClick={retryFn}>
          <RefreshCw className="h-4 w-4 mr-1" />
          Retry
        </Button>
      )}
    </div>
  );

  const renderEmptyState = (message: string, icon?: React.ReactNode) => (
    <div className="py-12 text-center">
      {icon || <Info className="h-8 w-8 text-gray-300 mx-auto mb-2" />}
      <p className="text-sm text-muted-foreground">{message}</p>
    </div>
  );

  // ---- Overview Tab ----
  const renderOverviewTab = () => (
    <div className="space-y-6 mt-6">
      {/* Case Information Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Briefcase className="h-5 w-5 text-emerald-600" />
            Case Information
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
            <div>
              <p className="text-xs text-muted-foreground mb-1">Case Number</p>
              <p className="text-sm font-medium">{caseData.case_number}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1">Case Type</p>
              <Badge variant={getCaseTypeVariant(caseData.case_type)} size="sm">
                {caseData.case_type?.replace(/_/g, ' ')}
              </Badge>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1">Status</p>
              <Badge variant={getStatusBadgeVariant(caseData.case_status)} size="sm">
                {caseData.case_status?.replace(/_/g, ' ')}
              </Badge>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1">Priority</p>
              <Badge variant={getPriorityBadgeVariant(caseData.priority)} size="sm">
                {caseData.priority}
              </Badge>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1">Subject</p>
              <p className="text-sm">{caseData.subject_name || '-'}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1">Assigned To</p>
              <p className="text-sm">{caseData.assigned_to_name || 'Unassigned'}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1">Opened Date</p>
              <p className="text-sm">{formatDate(caseData.opened_date)}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1">Target Resolution</p>
              <p className="text-sm">{formatDate(caseData.target_resolution_date)}</p>
            </div>
            {caseData.closed_date && (
              <div>
                <p className="text-xs text-muted-foreground mb-1">Closed Date</p>
                <p className="text-sm">{formatDate(caseData.closed_date)}</p>
              </div>
            )}
          </div>
          {caseData.description && (
            <div className="mt-4 pt-4 border-t">
              <p className="text-xs text-muted-foreground mb-1">Description</p>
              <p className="text-sm text-gray-700 dark:text-gray-300">{caseData.description}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Program Info Card */}
      {caseData.program_id && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ClipboardList className="h-5 w-5 text-blue-600" />
              Program Information
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div>
                <p className="text-xs text-muted-foreground mb-1">Program Name</p>
                <p className="text-sm font-medium">{caseData.program_name || '-'}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Program Type</p>
                <p className="text-sm">{caseData.program_type || '-'}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Workflow Progress */}
      {caseData.workflow_template_name && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-cyan-600" />
              Workflow Progress
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div>
                <p className="text-xs text-muted-foreground mb-1">Template</p>
                <p className="text-sm font-medium">{caseData.workflow_template_name}</p>
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <p className="text-xs text-muted-foreground">Progress</p>
                  <span className="text-sm font-bold">{caseData.workflow_progress ?? 0}%</span>
                </div>
                <Progress value={caseData.workflow_progress ?? 0} className="h-2" />
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Quick Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6 text-center">
            <ListTodo className="h-6 w-6 text-orange-500 mx-auto mb-2" />
            <p className="text-2xl font-bold">{caseData.pending_tasks}</p>
            <p className="text-xs text-muted-foreground">Pending Tasks</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6 text-center">
            <NoteIcon className="h-6 w-6 text-purple-500 mx-auto mb-2" />
            <p className="text-2xl font-bold">{caseData.notes_count}</p>
            <p className="text-xs text-muted-foreground">Notes</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6 text-center">
            <DocumentIcon className="h-6 w-6 text-blue-500 mx-auto mb-2" />
            <p className="text-2xl font-bold">{caseData.documents_count}</p>
            <p className="text-xs text-muted-foreground">Documents</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6 text-center">
            <ClockIcon className="h-6 w-6 text-gray-500 mx-auto mb-2" />
            <p className="text-2xl font-bold">{caseData.days_open}</p>
            <p className="text-xs text-muted-foreground">Days Open</p>
          </CardContent>
        </Card>
      </div>
    </div>
  );

  // ---- Activities Tab ----
  const renderActivitiesTab = () => {
    if (timelineLoading) return renderTabLoading();
    if (timelineError) return renderTabError(timelineError, () => { setLoadedTabs((p) => { const n = new Set(p); n.delete('activities'); return n; }); fetchTimeline(); });
    if (timeline.length === 0) return renderEmptyState('No activity events recorded yet.', <Activity className="h-8 w-8 text-gray-300 mx-auto mb-2" />);

    return (
      <div className="mt-6 space-y-1">
        {/* Timeline */}
        <div className="relative">
          {/* Vertical line */}
          <div className="absolute left-5 top-0 bottom-0 w-px bg-gray-200 dark:bg-gray-700" />

          {timeline.map((event, idx) => (
            <div key={event.event_id ?? idx} className="relative flex items-start gap-4 py-4 pl-2">
              {/* Icon circle */}
              <div className="relative z-10 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
                {getTimelineEventIcon(event.event_type)}
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge variant={getStatusBadgeVariant(event.event_type)} size="sm">
                    {event.event_type?.replace(/_/g, ' ')}
                  </Badge>
                  {event.actor_name && (
                    <span className="text-xs text-muted-foreground flex items-center gap-1">
                      <User className="h-3 w-3" />
                      {event.actor_name}
                    </span>
                  )}
                </div>
                <p className="text-sm text-gray-700 dark:text-gray-300 mt-1">{event.description}</p>
                <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                  <Calendar className="h-3 w-3" />
                  {formatDateTime(event.event_date)}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // ---- Documents Tab ----
  const renderDocumentsTab = () => {
    if (documentsLoading) return renderTabLoading();
    if (documentsError) return renderTabError(documentsError, () => { setLoadedTabs((p) => { const n = new Set(p); n.delete('documents'); return n; }); fetchDocuments(); });

    return (
      <div className="mt-6 space-y-4">
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-semibold">Documents</h3>
          <Button size="sm" onClick={() => setDocumentDialogOpen(true)}>
            <AddIcon className="h-4 w-4 mr-1" />
            Add Document
          </Button>
        </div>

        {documents.length === 0 ? (
          renderEmptyState('No documents attached to this case.', <DocumentIcon className="h-8 w-8 text-gray-300 mx-auto mb-2" />)
        ) : (
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Type</TableHead>
                  <TableHead>File Name</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Expiration</TableHead>
                  <TableHead>Added</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {documents.map((doc) => (
                  <TableRow key={doc.document_id}>
                    <TableCell>
                      <Badge variant="outline" size="sm">{doc.document_type}</Badge>
                    </TableCell>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        <DocumentIcon className="h-4 w-4 text-gray-400" />
                        {doc.file_name}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={getStatusBadgeVariant(doc.status)} size="sm">
                        {doc.status}
                      </Badge>
                    </TableCell>
                    <TableCell>{formatDate(doc.expiration_date)}</TableCell>
                    <TableCell>
                      <div>
                        <p className="text-sm">{formatDate(doc.created_at)}</p>
                        {doc.created_by_name && (
                          <p className="text-xs text-muted-foreground">{doc.created_by_name}</p>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    );
  };

  // ---- Notes Tab ----
  const renderNotesTab = () => {
    if (notesLoading) return renderTabLoading();
    if (notesError) return renderTabError(notesError, () => { setLoadedTabs((p) => { const n = new Set(p); n.delete('notes'); return n; }); fetchNotes(); });

    return (
      <div className="mt-6 space-y-4">
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-semibold">Notes</h3>
          <Button size="sm" onClick={() => setNoteDialogOpen(true)}>
            <AddIcon className="h-4 w-4 mr-1" />
            Add Note
          </Button>
        </div>

        {notes.length === 0 ? (
          renderEmptyState('No notes recorded yet.', <NoteIcon className="h-8 w-8 text-gray-300 mx-auto mb-2" />)
        ) : (
          <div className="space-y-3">
            {notes.map((note) => (
              <Card key={note.note_id}>
                <CardContent className="pt-4 pb-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge variant="outline" size="sm">{note.note_type}</Badge>
                      </div>
                      <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{note.note_text}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 mt-3 pt-3 border-t text-xs text-muted-foreground">
                    {note.created_by_name && (
                      <span className="flex items-center gap-1">
                        <User className="h-3 w-3" />
                        {note.created_by_name}
                      </span>
                    )}
                    <span className="flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      {formatDateTime(note.created_at)}
                    </span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    );
  };

  // ---- Goals Tab ----
  const renderGoalsTab = () => {
    if (goalsLoading) return renderTabLoading();
    if (goalsError) return renderTabError(goalsError, () => { setLoadedTabs((p) => { const n = new Set(p); n.delete('goals'); return n; }); fetchGoals(); });

    return (
      <div className="mt-6 space-y-4">
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-semibold">Goals</h3>
          <Button size="sm" onClick={() => setGoalDialogOpen(true)}>
            <AddIcon className="h-4 w-4 mr-1" />
            Add Goal
          </Button>
        </div>

        {goals.length === 0 ? (
          renderEmptyState('No goals set for this case.', <GoalIcon className="h-8 w-8 text-gray-300 mx-auto mb-2" />)
        ) : (
          <div className="space-y-4">
            {goals.map((goal) => (
              <Card key={goal.goal_id}>
                <CardContent className="pt-4 pb-4">
                  <div className="flex items-start justify-between gap-4 mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge variant={getCaseTypeVariant(goal.goal_category)} size="sm">
                          {goal.goal_category}
                        </Badge>
                        {goal.goal_type && (
                          <Badge variant="outline" size="sm">{goal.goal_type?.replace(/_/g, ' ')}</Badge>
                        )}
                        <Badge variant={getStatusBadgeVariant(goal.status)} size="sm">
                          {goal.status}
                        </Badge>
                      </div>
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{goal.goal_text}</p>
                    </div>
                    {goal.target_date && (
                      <div className="text-right flex-shrink-0">
                        <p className="text-xs text-muted-foreground">Target</p>
                        <p className="text-sm">{formatDate(goal.target_date)}</p>
                      </div>
                    )}
                  </div>
                  <div className="space-y-1">
                    <div className="flex items-center justify-between">
                      <p className="text-xs text-muted-foreground">Progress</p>
                      <span className="text-sm font-bold">{goal.progress ?? 0}%</span>
                    </div>
                    <Progress value={goal.progress ?? 0} className="h-2" />
                  </div>
                  {goal.completed_date && (
                    <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1">
                      <CheckCircleIcon className="h-3 w-3 text-emerald-500" />
                      Completed {formatDate(goal.completed_date)}
                    </p>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    );
  };

  // ---- Tracking Tab ----
  const renderTrackingTab = () => {
    if (trackingLoading) return renderTabLoading();
    if (trackingError) return renderTabError(trackingError, () => { setLoadedTabs((p) => { const n = new Set(p); n.delete('tracking'); return n; }); fetchTracking(); });

    return (
      <div className="mt-6 space-y-6">
        {/* Workflow Instances */}
        <div>
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <BarChart3 className="h-5 w-5 text-cyan-600" />
            Workflow Instances
          </h3>
          {workflows.length === 0 ? (
            renderEmptyState('No workflows associated with this case.')
          ) : (
            <div className="space-y-3">
              {workflows.map((wf) => (
                <Card key={wf.instance_id}>
                  <CardContent className="pt-4 pb-4">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <p className="text-sm font-medium">{wf.template_name}</p>
                        <p className="text-xs text-muted-foreground">
                          {wf.completed_steps} / {wf.total_steps} steps completed
                        </p>
                      </div>
                      <Badge variant={getStatusBadgeVariant(wf.status)} size="sm">
                        {wf.status?.replace(/_/g, ' ')}
                      </Badge>
                    </div>
                    <div className="space-y-1">
                      <div className="flex items-center justify-between">
                        <p className="text-xs text-muted-foreground">Progress</p>
                        <span className="text-sm font-bold">{wf.progress ?? 0}%</span>
                      </div>
                      <Progress value={wf.progress ?? 0} className="h-2" />
                    </div>
                    <div className="flex gap-4 mt-2 text-xs text-muted-foreground">
                      {wf.started_at && (
                        <span>Started: {formatDate(wf.started_at)}</span>
                      )}
                      {wf.completed_at && (
                        <span>Completed: {formatDate(wf.completed_at)}</span>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Tasks */}
        <div>
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <ListTodo className="h-5 w-5 text-orange-600" />
            Tasks
          </h3>
          {tasks.length === 0 ? (
            renderEmptyState('No tasks for this case.')
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Task</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Priority</TableHead>
                    <TableHead>Due Date</TableHead>
                    <TableHead>Assigned To</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {tasks.map((task) => (
                    <TableRow key={task.task_id}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {task.status === 'completed' ? (
                            <CheckCircleIcon className="h-4 w-4 text-emerald-500" />
                          ) : task.is_overdue ? (
                            <AlertCircle className="h-4 w-4 text-red-500" />
                          ) : (
                            <CircleDot className="h-4 w-4 text-gray-400" />
                          )}
                          <span className={task.status === 'completed' ? 'line-through text-muted-foreground' : ''}>
                            {task.task_title}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" size="sm">{task.task_type}</Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant={getStatusBadgeVariant(task.is_overdue && task.status !== 'completed' ? 'overdue' : task.status)} size="sm">
                          {task.is_overdue && task.status !== 'completed' ? 'Overdue' : task.status?.replace(/_/g, ' ')}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant={getPriorityBadgeVariant(task.priority)} size="sm">
                          {task.priority}
                        </Badge>
                      </TableCell>
                      <TableCell>{formatDate(task.due_date)}</TableCell>
                      <TableCell>{task.assigned_to_name || '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </div>
    );
  };

  // ---- Communications Tab ----
  const renderCommunicationsTab = () => {
    if (communicationsLoading) return renderTabLoading();
    if (communicationsError) return renderTabError(communicationsError, () => { setLoadedTabs((p) => { const n = new Set(p); n.delete('communications'); return n; }); fetchCommunications(); });

    const getCommIcon = (type: string) => {
      switch (type?.toLowerCase()) {
        case 'phone':
        case 'call':
          return <PhoneIcon className="h-4 w-4 text-green-500" />;
        case 'email':
          return <EmailIcon className="h-4 w-4 text-blue-500" />;
        case 'sms':
        case 'text':
          return <MessageIcon className="h-4 w-4 text-purple-500" />;
        default:
          return <MessageIcon className="h-4 w-4 text-gray-400" />;
      }
    };

    const getDirectionIcon = (direction: string) => {
      if (direction === 'inbound') return <ArrowDownCircle className="h-3 w-3 text-blue-400" />;
      return <ArrowUpCircle className="h-3 w-3 text-emerald-400" />;
    };

    return (
      <div className="mt-6 space-y-4">
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-semibold">Communications</h3>
          <Button size="sm" onClick={() => setCommunicationDialogOpen(true)}>
            <AddIcon className="h-4 w-4 mr-1" />
            Log Communication
          </Button>
        </div>

        {communications.length === 0 ? (
          renderEmptyState('No communications logged yet.', <MessageIcon className="h-8 w-8 text-gray-300 mx-auto mb-2" />)
        ) : (
          <div className="space-y-3">
            {communications.map((comm) => (
              <Card key={comm.communication_id}>
                <CardContent className="pt-4 pb-4">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 mt-1">
                      {getCommIcon(comm.communication_type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <Badge variant="outline" size="sm">
                          {comm.communication_type}
                        </Badge>
                        <span className="flex items-center gap-1 text-xs text-muted-foreground">
                          {getDirectionIcon(comm.direction)}
                          {comm.direction}
                        </span>
                        {comm.contact_name && (
                          <span className="text-xs text-muted-foreground flex items-center gap-1">
                            <User className="h-3 w-3" />
                            {comm.contact_name}
                          </span>
                        )}
                      </div>
                      {comm.subject && (
                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{comm.subject}</p>
                      )}
                      {comm.content && (
                        <p className="text-sm text-gray-700 dark:text-gray-300 mt-1 whitespace-pre-wrap">{comm.content}</p>
                      )}
                      {comm.outcome && (
                        <p className="text-xs mt-2 text-muted-foreground">
                          <span className="font-medium">Outcome:</span> {comm.outcome}
                        </p>
                      )}
                      <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                        {comm.created_by_name && (
                          <span>{comm.created_by_name}</span>
                        )}
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {formatDateTime(comm.created_at)}
                        </span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    );
  };

  // ---- Assessments & Forms Tab ----
  const renderAssessmentsTab = () => {
    if (assessmentsLoading) return renderTabLoading();
    if (assessmentsError) return renderTabError(assessmentsError, () => { setLoadedTabs((p) => { const n = new Set(p); n.delete('assessments'); return n; }); fetchAssessmentsAndForms(); });

    return (
      <div className="mt-6 space-y-6">
        {/* Assessments Section */}
        <div>
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <ClipboardList className="h-5 w-5 text-orange-600" />
              Assessments
            </h3>
            <Button size="sm" onClick={() => setAssessmentDialogOpen(true)}>
              <AddIcon className="h-4 w-4 mr-1" />
              Add Assessment
            </Button>
          </div>

          {assessments.length === 0 ? (
            renderEmptyState('No assessments recorded.')
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Score</TableHead>
                    <TableHead>Result</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Assessor</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {assessments.map((assessment) => (
                    <TableRow key={assessment.assessment_id}>
                      <TableCell className="font-medium">{assessment.assessment_name}</TableCell>
                      <TableCell>
                        <Badge variant="outline" size="sm">{assessment.assessment_type}</Badge>
                      </TableCell>
                      <TableCell>
                        {assessment.score !== null && assessment.max_score !== null ? (
                          <span className="text-sm">
                            {assessment.score} / {assessment.max_score}
                          </span>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell>
                        {assessment.pass_fail ? (
                          <Badge
                            variant={assessment.pass_fail === 'pass' ? 'success' : 'danger'}
                            size="sm"
                          >
                            {assessment.pass_fail}
                          </Badge>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant={getStatusBadgeVariant(assessment.status)} size="sm">
                          {assessment.status}
                        </Badge>
                      </TableCell>
                      <TableCell>{formatDate(assessment.completed_date)}</TableCell>
                      <TableCell>{assessment.assessor_name || '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>

        {/* Forms Section */}
        <div>
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <FileCheck className="h-5 w-5 text-blue-600" />
            Forms
          </h3>

          {forms.length === 0 ? (
            renderEmptyState('No forms submitted for this case.')
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Form Name</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Submitted</TableHead>
                    <TableHead>Submitted By</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {forms.map((form) => (
                    <TableRow key={form.form_id}>
                      <TableCell className="font-medium">{form.form_name}</TableCell>
                      <TableCell>
                        <Badge variant="outline" size="sm">{form.form_type}</Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant={getStatusBadgeVariant(form.status)} size="sm">
                          {form.status}
                        </Badge>
                      </TableCell>
                      <TableCell>{formatDate(form.submitted_date)}</TableCell>
                      <TableCell>{form.submitted_by_name || '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </div>
    );
  };

  // ---------------------------------------------------------------------------
  // Dialogs
  // ---------------------------------------------------------------------------

  const renderDocumentDialog = () => (
    <Dialog open={documentDialogOpen} onOpenChange={setDocumentDialogOpen}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Add Document</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="doc-type">Document Type</Label>
            <Select
              value={documentForm.document_type}
              onValueChange={(val) => setDocumentForm({ ...documentForm, document_type: val })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="general">General</SelectItem>
                <SelectItem value="identification">Identification</SelectItem>
                <SelectItem value="certification">Certification</SelectItem>
                <SelectItem value="license">License</SelectItem>
                <SelectItem value="training_record">Training Record</SelectItem>
                <SelectItem value="background_check">Background Check</SelectItem>
                <SelectItem value="resume">Resume</SelectItem>
                <SelectItem value="reference">Reference</SelectItem>
                <SelectItem value="contract">Contract</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="doc-filename">File Name</Label>
            <Input
              id="doc-filename"
              value={documentForm.file_name}
              onChange={(e) => setDocumentForm({ ...documentForm, file_name: e.target.value })}
              placeholder="e.g., background_check_2025.pdf"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="doc-notes">Notes</Label>
            <Textarea
              id="doc-notes"
              value={documentForm.notes}
              onChange={(e) => setDocumentForm({ ...documentForm, notes: e.target.value })}
              placeholder="Optional notes about this document..."
              rows={3}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setDocumentDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleAddDocument}
            disabled={!documentForm.file_name.trim() || submitting}
          >
            {submitting && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
            Add Document
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  const renderNoteDialog = () => (
    <Dialog open={noteDialogOpen} onOpenChange={setNoteDialogOpen}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Add Note</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="note-type">Note Type</Label>
            <Select
              value={noteForm.note_type}
              onValueChange={(val) => setNoteForm({ ...noteForm, note_type: val })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="general">General</SelectItem>
                <SelectItem value="progress">Progress Update</SelectItem>
                <SelectItem value="clinical">Clinical</SelectItem>
                <SelectItem value="follow_up">Follow-Up</SelectItem>
                <SelectItem value="administrative">Administrative</SelectItem>
                <SelectItem value="internal">Internal</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="note-text">Note</Label>
            <Textarea
              id="note-text"
              value={noteForm.note_text}
              onChange={(e) => setNoteForm({ ...noteForm, note_text: e.target.value })}
              placeholder="Enter your note..."
              rows={5}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setNoteDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleAddNote}
            disabled={!noteForm.note_text.trim() || submitting}
          >
            {submitting && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
            Add Note
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  const renderGoalDialog = () => (
    <Dialog open={goalDialogOpen} onOpenChange={setGoalDialogOpen}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Add Goal</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="goal-text">Goal</Label>
            <Textarea
              id="goal-text"
              value={goalForm.goal_text}
              onChange={(e) => setGoalForm({ ...goalForm, goal_text: e.target.value })}
              placeholder="Describe the goal..."
              rows={3}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="goal-category">Category</Label>
            <Select
              value={goalForm.goal_category}
              onValueChange={(val) => setGoalForm({ ...goalForm, goal_category: val })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="professional">Professional</SelectItem>
                <SelectItem value="training">Training</SelectItem>
                <SelectItem value="certification">Certification</SelectItem>
                <SelectItem value="performance">Performance</SelectItem>
                <SelectItem value="compliance">Compliance</SelectItem>
                <SelectItem value="health">Health</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="goal-type">Goal Type</Label>
            <Select
              value={goalForm.goal_type}
              onValueChange={(val) => setGoalForm({ ...goalForm, goal_type: val })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="short_term">Short Term</SelectItem>
                <SelectItem value="long_term">Long Term</SelectItem>
                <SelectItem value="milestone">Milestone</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="goal-target-date">Target Date</Label>
            <Input
              id="goal-target-date"
              type="date"
              value={goalForm.target_date}
              onChange={(e) => setGoalForm({ ...goalForm, target_date: e.target.value })}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setGoalDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleAddGoal}
            disabled={!goalForm.goal_text.trim() || submitting}
          >
            {submitting && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
            Add Goal
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  const renderCommunicationDialog = () => (
    <Dialog open={communicationDialogOpen} onOpenChange={setCommunicationDialogOpen}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Log Communication</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="comm-type">Type</Label>
              <Select
                value={communicationForm.communication_type}
                onValueChange={(val) => setCommunicationForm({ ...communicationForm, communication_type: val })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="email">Email</SelectItem>
                  <SelectItem value="phone">Phone</SelectItem>
                  <SelectItem value="sms">SMS</SelectItem>
                  <SelectItem value="in_person">In Person</SelectItem>
                  <SelectItem value="video">Video Call</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="comm-direction">Direction</Label>
              <Select
                value={communicationForm.direction}
                onValueChange={(val) => setCommunicationForm({ ...communicationForm, direction: val })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select direction" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="outbound">Outbound</SelectItem>
                  <SelectItem value="inbound">Inbound</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="comm-contact">Contact Name</Label>
            <Input
              id="comm-contact"
              value={communicationForm.contact_name}
              onChange={(e) => setCommunicationForm({ ...communicationForm, contact_name: e.target.value })}
              placeholder="Name of the person contacted"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="comm-subject">Subject</Label>
            <Input
              id="comm-subject"
              value={communicationForm.subject}
              onChange={(e) => setCommunicationForm({ ...communicationForm, subject: e.target.value })}
              placeholder="Communication subject"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="comm-content">Content</Label>
            <Textarea
              id="comm-content"
              value={communicationForm.content}
              onChange={(e) => setCommunicationForm({ ...communicationForm, content: e.target.value })}
              placeholder="Communication details..."
              rows={4}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="comm-outcome">Outcome</Label>
            <Input
              id="comm-outcome"
              value={communicationForm.outcome}
              onChange={(e) => setCommunicationForm({ ...communicationForm, outcome: e.target.value })}
              placeholder="e.g., Scheduled follow-up, Left voicemail, Confirmed"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setCommunicationDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleLogCommunication}
            disabled={!communicationForm.content?.trim() || submitting}
          >
            {submitting && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
            <Send className="h-4 w-4 mr-1" />
            Log Communication
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  const renderAssessmentDialog = () => (
    <Dialog open={assessmentDialogOpen} onOpenChange={setAssessmentDialogOpen}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Add Assessment</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="assess-type">Assessment Type</Label>
            <Select
              value={assessmentForm.assessment_type}
              onValueChange={(val) => setAssessmentForm({ ...assessmentForm, assessment_type: val })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="skills">Skills Assessment</SelectItem>
                <SelectItem value="competency">Competency Evaluation</SelectItem>
                <SelectItem value="performance">Performance Review</SelectItem>
                <SelectItem value="clinical">Clinical Assessment</SelectItem>
                <SelectItem value="compliance">Compliance Check</SelectItem>
                <SelectItem value="certification_exam">Certification Exam</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="assess-name">Assessment Name</Label>
            <Input
              id="assess-name"
              value={assessmentForm.assessment_name}
              onChange={(e) => setAssessmentForm({ ...assessmentForm, assessment_name: e.target.value })}
              placeholder="e.g., CPR Certification Exam"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="assess-score">Score</Label>
              <Input
                id="assess-score"
                type="number"
                value={assessmentForm.score}
                onChange={(e) => setAssessmentForm({ ...assessmentForm, score: e.target.value })}
                placeholder="e.g., 85"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="assess-max-score">Max Score</Label>
              <Input
                id="assess-max-score"
                type="number"
                value={assessmentForm.max_score}
                onChange={(e) => setAssessmentForm({ ...assessmentForm, max_score: e.target.value })}
                placeholder="e.g., 100"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="assess-result">Pass / Fail</Label>
            <Select
              value={assessmentForm.pass_fail}
              onValueChange={(val) => setAssessmentForm({ ...assessmentForm, pass_fail: val })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select result (optional)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="">Not Applicable</SelectItem>
                <SelectItem value="pass">Pass</SelectItem>
                <SelectItem value="fail">Fail</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setAssessmentDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleAddAssessment}
            disabled={!assessmentForm.assessment_name.trim() || submitting}
          >
            {submitting && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
            Add Assessment
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  // ---------------------------------------------------------------------------
  // Main render
  // ---------------------------------------------------------------------------

  return (
    <div className="p-4 md:p-6 max-w-[1400px] mx-auto">
      {/* Header */}
      <div className="mb-6 flex flex-col md:flex-row gap-4 md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-1">
            Case Details
          </h1>
          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-sm text-muted-foreground">{caseData.case_number}</span>
            <Badge variant={getCaseTypeVariant(caseData.case_type)} size="sm">
              {caseData.case_type?.replace(/_/g, ' ')}
            </Badge>
            <Badge variant={getStatusBadgeVariant(caseData.case_status)} size="sm">
              {caseData.case_status?.replace(/_/g, ' ')}
            </Badge>
            <Badge variant={getPriorityBadgeVariant(caseData.priority)} size="sm">
              {caseData.priority}
            </Badge>
          </div>
          <p className="text-muted-foreground mt-1">
            {caseData.title}
            {caseData.subject_name ? ` - ${caseData.subject_name}` : ''}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {onBack && (
            <Button variant="outline" onClick={onBack}>
              <BackIcon className="h-4 w-4 mr-2" />
              Back to List
            </Button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <div className="overflow-x-auto scrollbar-thin scrollbar-thumb-gray-300">
          <TabsList className="w-max min-w-full justify-start flex-nowrap">
            <TabsTrigger value="overview" className="whitespace-nowrap text-xs sm:text-sm px-2 sm:px-4">
              Overview
            </TabsTrigger>
            <TabsTrigger value="activities" className="flex items-center gap-1 sm:gap-2 whitespace-nowrap text-xs sm:text-sm px-2 sm:px-4">
              <span className="hidden sm:inline">Activities</span>
              <span className="sm:hidden">Activity</span>
            </TabsTrigger>
            <TabsTrigger value="documents" className="flex items-center gap-1 sm:gap-2 whitespace-nowrap text-xs sm:text-sm px-2 sm:px-4">
              <span className="hidden sm:inline">Documents</span>
              <span className="sm:hidden">Docs</span>
              {caseData.documents_count > 0 && (
                <Badge variant="secondary" size="sm" className="h-5 px-1.5 text-xs">
                  {caseData.documents_count}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="notes" className="flex items-center gap-1 sm:gap-2 whitespace-nowrap text-xs sm:text-sm px-2 sm:px-4">
              Notes
              {caseData.notes_count > 0 && (
                <Badge variant="secondary" size="sm" className="h-5 px-1.5 text-xs">
                  {caseData.notes_count}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="goals" className="flex items-center gap-1 sm:gap-2 whitespace-nowrap text-xs sm:text-sm px-2 sm:px-4">
              Goals
            </TabsTrigger>
            <TabsTrigger value="tracking" className="flex items-center gap-1 sm:gap-2 whitespace-nowrap text-xs sm:text-sm px-2 sm:px-4">
              <span className="hidden sm:inline">Tracking</span>
              <span className="sm:hidden">Track</span>
              {caseData.pending_tasks > 0 && (
                <Badge variant="danger" size="sm" className="h-5 px-1.5 text-xs">
                  {caseData.pending_tasks}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="communications" className="flex items-center gap-1 sm:gap-2 whitespace-nowrap text-xs sm:text-sm px-2 sm:px-4">
              <span className="hidden sm:inline">Communications</span>
              <span className="sm:hidden">Comms</span>
            </TabsTrigger>
            <TabsTrigger value="assessments" className="flex items-center gap-1 sm:gap-2 whitespace-nowrap text-xs sm:text-sm px-2 sm:px-4">
              <span className="hidden sm:inline">Assessments & Forms</span>
              <span className="sm:hidden">Assess</span>
            </TabsTrigger>
          </TabsList>
        </div>

        {/* Tab Content */}
        <TabsContent value="overview">
          {renderOverviewTab()}
        </TabsContent>

        <TabsContent value="activities">
          {renderActivitiesTab()}
        </TabsContent>

        <TabsContent value="documents">
          {renderDocumentsTab()}
        </TabsContent>

        <TabsContent value="notes">
          {renderNotesTab()}
        </TabsContent>

        <TabsContent value="goals">
          {renderGoalsTab()}
        </TabsContent>

        <TabsContent value="tracking">
          {renderTrackingTab()}
        </TabsContent>

        <TabsContent value="communications">
          {renderCommunicationsTab()}
        </TabsContent>

        <TabsContent value="assessments">
          {renderAssessmentsTab()}
        </TabsContent>
      </Tabs>

      {/* Dialogs */}
      {renderDocumentDialog()}
      {renderNoteDialog()}
      {renderGoalDialog()}
      {renderCommunicationDialog()}
      {renderAssessmentDialog()}
    </div>
  );
};

export default CaseDetail;
